import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from '@/config/site';
import { parseLocalizedPath } from '@/lib/localizedPath';
import { getLocaleMeta } from '@/i18n/locale-meta';

/**
 * Reads the URL path on every navigation, derives the active locale, and
 * pushes it through to:
 *   - i18next (`changeLanguage` so `t(...)` resolves the right namespace)
 *   - <html lang> + <html dir> (a11y + RTL plumbing)
 *
 * Renders nothing — this is a side-effect component mounted near the root
 * of the routing tree. Full path-prefixed routing arrives in Phase 1c
 * along with the locale switcher; until then this component still does
 * the right thing for any tenant whose only locale is DEFAULT_LOCALE.
 */
export default function LocaleResolver() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const { locale } = parseLocalizedPath(pathname);
    const resolved = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
    if (i18n.language !== resolved) {
      void i18n.changeLanguage(resolved);
    }
    const meta = getLocaleMeta(resolved);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = resolved;
      document.documentElement.dir = meta.dir;
    }
  }, [pathname, i18n]);

  return null;
}
